<template>
  <div>
    <div class="tabs flex border-b mb-4">
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'patient-complaint',
          'text-gray-500': currentTab !== 'patient-complaint',
        }"
        @click="currentTab = 'patient-complaint'"
      >
        Patient Complaint
      </button>
    </div>

    <div v-if="state === 'create' || state === 'edit'" class="grid grid-cols-1 gap-4">
      <div v-if="showMenstural" class="flex flex-col">
        <label class="font-semibold text-sm">Latest Menstrual Period</label>
        <input
          :disabled="state === 'view'"
          v-model="presentingComplainForm.menstrual_period"
          type="date"
          class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
        />
        <span class="text-red-700 text-sm" v-if="errors.menstrual_period">
          Menstrual period is required
        </span>
      </div>

      <!-- ── Symptoms list ── -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="font-semibold text-sm">Presenting Complaints</label>
          <button
            type="button"
            @click="addSymptom"
            class="text-xs px-3 py-1 rounded border border-custom-blue text-custom-blue hover:bg-blue-50"
          >
            + Add Symptom
          </button>
        </div>

        <div
          v-for="(symptom, index) in presentingComplainForm.symptoms"
          :key="index"
          class="grid grid-cols-12 gap-2 mb-2 items-start"
        >
          <!-- Chief complaint -->
          <div class="col-span-5">
            <input
              v-model="symptom.chief_complaint"
              type="text"
              :placeholder="index === 0 ? 'e.g. Severe stomach pains' : 'Symptom'"
              class="w-full border-gray-300 rounded p-2 bg-gray-50 text-sm"
            />
          </div>

          <!-- Duration number -->
          <div class="col-span-2">
            <input
              v-model.number="symptom.duration"
              type="number"
              placeholder="2"
              min="1"
              class="w-full border-gray-300 rounded p-2 bg-gray-50 text-sm"
            />
          </div>

          <!-- Duration type -->
          <div class="col-span-4">
            <select
              v-model="symptom.duration_type"
              class="w-full border-gray-300 rounded p-2 bg-gray-50 text-sm"
            >
              <option value="" disabled selected>Period</option>
              <option value="Hour(s)">Hour(s)</option>
              <option value="Day(s)">Day(s)</option>
              <option value="Week(s)">Week(s)</option>
              <option value="Month(s)">Month(s)</option>
              <option value="Year(s)">Year(s)</option>
            </select>
          </div>

          <!-- Remove button (only shown when more than one row) -->
          <div class="col-span-1 flex items-center justify-center">
            <button
              v-if="presentingComplainForm.symptoms.length > 1"
              type="button"
              @click="removeSymptom(index)"
              class="text-red-400 hover:text-red-600 text-lg leading-none"
              title="Remove"
            >
              &times;
            </button>
          </div>
        </div>

        <span class="text-red-700 text-sm" v-if="errors.symptoms">
          At least one complaint with duration is required
        </span>
      </div>

      <div class="flex flex-col">
        <label class="font-semibold text-sm">
          History of Illness
        </label>
        <textarea
          :disabled="state === 'view'"
          v-model="presentingComplainForm.history_of_illness"
          rows="2"
          placeholder="No fever, nausea but no vomiting."
          class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
        ></textarea>
        <span class="text-red-700 text-sm" v-if="errors.history_of_illness">
          History of illness is required
        </span>
      </div>

      <div class="flex flex-col">
        <label class="font-semibold text-sm">
          Social History
        </label>
        <vue-multiselect
          v-model="presentingComplainForm.social"
          :options="social_histories"
          :multiple="true"
          :taggable="true"
          @tag="addSocialTag"
          placeholder="Type to add social history..."
          class="w-full"
        />
        <span class="text-red-700 text-sm" v-if="errors.social">
          Social history is required
        </span>
      </div>

      <div class="flex flex-col">
        <label class="font-semibold text-sm">Family History</label>
        <vue-multiselect
          v-model="presentingComplainForm.family_history"
          :options="family_histories"
          :multiple="true"
          :taggable="true"
          @tag="addFamilyTag"
          placeholder="Type to add family history..."
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="font-semibold text-sm">
          Preliminary Diagnosis
        </label>
        <multiselect
          v-model="presentingComplainForm.diagnosis"
          :options="diagnosis"
          :multiple="true"
          :searchable="true"
          :loading="isLoading"
          :internal-search="false"
          @search-change="searchDiagnosis"
          placeholder="Type to search diagnosis and select"
          label="name"
          track-by="id"
        ></multiselect>
        <span class="text-red-700 text-sm" v-if="errors.diagnosis">
          Preliminary diagnosis is required
        </span>
      </div>
    </div>

    <!-- ── View mode ── -->
    <div v-if="state === 'view'" class="grid grid-cols-2 px-4 gap-4 mb-6">
      <div>
        <p class="font-semibold">Last Menstrual Period:</p>
        <p>{{ existingComplaint.menstrual_period || "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">History of Illness:</p>
        <p>{{ existingComplaint.history_of_illness || "-" }}</p>
      </div>

      <!-- Complaints list in view mode -->
      <div class="col-span-2">
        <p class="font-semibold mb-1">Presenting Complaints:</p>
        <div v-if="viewSymptoms.length" class="space-y-1">
          <div
            v-for="(s, i) in viewSymptoms"
            :key="i"
            class="flex items-center gap-2 text-sm"
          >
            <span class="w-2 h-2 rounded-full bg-custom-blue inline-block shrink-0"></span>
            <span>{{ s.chief_complaint }}</span>
            <span v-if="s.duration" class="text-gray-500">— {{ s.duration }} {{ s.duration_type }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-gray-400">-</p>
      </div>

      <div>
        <p class="font-semibold">Social History:</p>
        <p>
          <span v-for="(history, index) in existingSocialHistories" :key="index">
            {{ history }}<span v-if="index < existingSocialHistories.length - 1">, </span>
          </span>
          <span v-if="!existingSocialHistories.length">-</span>
        </p>
      </div>
      <div>
        <p class="font-semibold">Family History:</p>
        <p>
          <span v-for="(item, index) in existingFamilyHistories" :key="index">
            {{ item }}<span v-if="index < existingFamilyHistories.length - 1">, </span>
          </span>
          <span v-if="!existingFamilyHistories.length">-</span>
        </p>
      </div>
      <div class="col-span-2">
        <p class="font-semibold">Preliminary Diagnosis:</p>
        <p>
          <span v-for="(diag, index) in existingPreliminaryDiagnosis" :key="index">
            {{ diag.diagnosis?.name || "-" }}<span v-if="index < existingPreliminaryDiagnosis.length - 1">, </span>
          </span>
          <span v-if="!existingPreliminaryDiagnosis.length">-</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import Multiselect from "vue-multiselect";
import VueMultiselect from "vue-multiselect";

const emptySymptom = () => ({ chief_complaint: "", duration: "", duration_type: "" });

export default {
  name: "PresentingComplaint",
  components: {
    Multiselect,
    VueMultiselect,
  },
  props: {
    state: {
      type: String,
      required: true,
      validator: value => ["create", "edit", "view"].includes(value),
    },
    visitId: {
      type: String,
      required: true,
    },
    existingComplaint: {
      type: Object,
      default: () => ({}),
    },
    existingPreliminaryDiagnosis: {
      type: Array,
      default: () => [],
    },
    existingSocialHistories: {
      type: Array,
      default: () => [],
    },
    existingFamilyHistories: {
      type: Array,
      default: () => [],
    },
    patientGender: {
      type: String,
      default: "",
    },
    optional: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentTab: "patient-complaint",
      presentingComplainForm: {
        menstrual_period: "",
        symptoms: [emptySymptom()],
        history_of_illness: "",
        social: [],
        family_history: [],
        diagnosis: [],
      },
      diagnosis: [],
      social_histories: [],
      family_histories: [],
      errors: {},
      showMenstural: false,
      isLoading: false,
    };
  },
  computed: {
    // Symptoms to display in view mode — prefer the new array, fall back to legacy single fields
    viewSymptoms() {
      const existing = this.existingComplaint;
      if (existing?.symptoms?.length) return existing.symptoms;
      if (existing?.chief_complaint) {
        return [{ chief_complaint: existing.chief_complaint, duration: existing.duration, duration_type: existing.duration_type }];
      }
      return [];
    },
  },
  watch: {
    patientGender: {
      handler(gender) {
        this.showMenstural = gender === "Female";
      },
      immediate: true,
    },
    existingComplaint: {
      handler(newItem) {
        if (newItem && Object.keys(newItem).length > 0) {
          // Prefer the new symptoms array; fall back to legacy single fields
          const symptoms = newItem.symptoms?.length
            ? newItem.symptoms.map(s => ({ ...s }))
            : newItem.chief_complaint
              ? [{ chief_complaint: newItem.chief_complaint, duration: newItem.duration, duration_type: newItem.duration_type }]
              : [emptySymptom()];

          this.presentingComplainForm = {
            ...this.presentingComplainForm,
            menstrual_period:   newItem.menstrual_period || (this.showMenstural ? this.getTodayDate() : ""),
            history_of_illness: newItem.history_of_illness || "",
            symptoms,
          };
        } else if (this.showMenstural) {
          this.presentingComplainForm.menstrual_period = this.getTodayDate();
        }
      },
      immediate: true,
      deep: true,
    },
    existingPreliminaryDiagnosis: {
      handler(newItems) {
        if (newItems?.length > 0) {
          this.diagnosis = newItems.map(d => d.diagnosis).filter(d => d);
          this.presentingComplainForm.diagnosis = [...this.diagnosis];
        }
      },
      immediate: true,
      deep: true,
    },
    existingSocialHistories: {
      handler(newItems) {
        if (newItems?.length > 0) {
          this.social_histories = [...newItems];
          this.presentingComplainForm.social = [...newItems];
        }
      },
      immediate: true,
      deep: true,
    },
    existingFamilyHistories: {
      handler(newItems) {
        if (newItems?.length > 0) {
          this.family_histories = [...newItems];
          this.presentingComplainForm.family_history = [...newItems];
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getTodayDate() {
      const today = new Date();
      const year  = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day   = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    addSymptom() {
      this.presentingComplainForm.symptoms.push(emptySymptom());
    },

    removeSymptom(index) {
      this.presentingComplainForm.symptoms.splice(index, 1);
    },

    submitPresentingComplaint() {
      if (this.optional) {
        this.errors = {};
        return { ...this.presentingComplainForm };
      }
      if (!this.validPresentingComplaint()) {
        return {};
      }
      return { ...this.presentingComplainForm };
    },

    addSocialTag(newTag) {
      if (newTag && !this.social_histories.includes(newTag)) {
        this.social_histories.push(newTag);
        this.presentingComplainForm.social.push(newTag);
      }
    },
    addFamilyTag(newTag) {
      if (newTag && !this.family_histories.includes(newTag)) {
        this.family_histories.push(newTag);
        this.presentingComplainForm.family_history.push(newTag);
      }
    },
    async searchDiagnosis(searchString) {
      if (searchString.length < 3) return;
      try {
        this.isLoading = true;
        const payload  = { name: searchString };
        const response = await apiClient.searchDiagnosis(payload);
        if (response.success) {
          this.diagnosis = response.data;
        } else {
          this.$toast.error("Unable to search diagnosis.");
        }
      } catch (error) {
        this.$toast.error("Unable to search diagnosis, please check your internet connection.");
      } finally {
        this.isLoading = false;
      }
    },
    validPresentingComplaint() {
      if (this.optional) return true;

      this.errors = {};
      let isValid = true;

      const hasValidSymptom = this.presentingComplainForm.symptoms.some(
        s => s.chief_complaint?.trim() && s.duration && s.duration_type
      );
      if (!hasValidSymptom) {
        this.errors.symptoms = "At least one complaint with duration is required";
        isValid = false;
      }

      const requiredFields = [
        { key: "history_of_illness", message: "History of illness is required"   },
        { key: "social",             message: "Social history is required"       },
        { key: "diagnosis",          message: "Preliminary diagnosis is required" },
      ];

      if (this.showMenstural && !this.presentingComplainForm.menstrual_period) {
        this.errors.menstrual_period = "Menstrual period is required";
        isValid = false;
      }

      requiredFields.forEach(field => {
        if (!this.presentingComplainForm[field.key] || (Array.isArray(this.presentingComplainForm[field.key]) && this.presentingComplainForm[field.key].length === 0)) {
          this.errors[field.key] = field.message;
          isValid = false;
        }
      });

      return isValid;
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>